import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle } from 'lucide-react';
import { useDomain } from '@/lib/domain-context';
import { getPostLoginRedirect } from '@/lib/auth-utils';

/**
 * AuthCallback — handles Firebase action URLs.
 *
 * Firebase email links include query params: mode, oobCode, apiKey.
 * - mode=verifyEmail -> apply action code to verify email
 * - mode=resetPassword -> redirect to reset password page with oobCode
 * - mode=recoverEmail -> apply action code to recover email
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { currentDomain } = useDomain();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const processFirebaseAction = async () => {
      try {
        const mode = searchParams.get('mode');
        const oobCode = searchParams.get('oobCode');

        if (!mode || !oobCode) {
          // No Firebase action params — check if user is already authenticated
          if (firebaseAuth.currentUser) {
            setStatus('success');
            const redirectPath = getPostLoginRedirect(currentDomain);
            setTimeout(() => { window.location.href = redirectPath; }, 500);
          } else {
            setErrorMessage('No authentication action found');
            setStatus('error');
            setTimeout(() => navigate('/auth'), 3000);
          }
          return;
        }

        switch (mode) {
          case 'verifyEmail': {
            await applyActionCode(firebaseAuth, oobCode);
            setStatus('success');
            // User isn't signed in yet (server-side signup doesn't create a client session).
            // Redirect to sign-in so they can log in with their verified account.
            setTimeout(() => { window.location.href = '/auth?tab=signin'; }, 2000);
            break;
          }

          case 'resetPassword': {
            // Redirect to reset password page with the oobCode
            navigate(`/auth/update-password?oobCode=${oobCode}`, { replace: true });
            return;
          }

          case 'recoverEmail': {
            await applyActionCode(firebaseAuth, oobCode);
            setStatus('success');
            setTimeout(() => navigate('/auth'), 2000);
            break;
          }

          default: {
            setErrorMessage(`Unknown action: ${mode}`);
            setStatus('error');
            setTimeout(() => navigate('/auth'), 3000);
          }
        }
      } catch (err: unknown) {
        console.error('Auth callback processing error:', err);

        const errCode = err instanceof Error && 'code' in err ? (err as any).code : undefined;
        let message = 'Failed to process authentication';
        if (errCode === 'auth/expired-action-code') {
          message = 'This link has expired. Please request a new one.';
        } else if (errCode === 'auth/invalid-action-code') {
          message = 'This link is invalid or has already been used.';
        }

        setErrorMessage(message);
        setStatus('error');
        setTimeout(() => navigate('/auth'), 3000);
      }
    };

    processFirebaseAction();
  }, [navigate, currentDomain, searchParams]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center animated-gradient p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            {status === 'processing' && (
              <>
                <LoadingSpinner size="lg" className="mx-auto" />
                <h2 className="text-xl font-semibold">Confirming your account...</h2>
                <p className="text-gray-600 dark:text-gray-400">Please wait while we verify your email.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                <h2 className="text-xl font-semibold text-green-700 dark:text-green-400">Welcome to JD Simplified!</h2>
                <p className="text-gray-600 dark:text-gray-400">Your email has been verified. Redirecting you to sign in...</p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                <h2 className="text-xl font-semibold text-red-700 dark:text-red-400">Confirmation Failed</h2>
                <p className="text-gray-600 dark:text-gray-400">{errorMessage}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting you back to sign in...</p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
